import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import {
	LUMINA_ANALYTICS_CHANNEL,
	type LuminaInteractedAttributesType,
	LuminaLayout,
	type LuminaSubAgentResponseType,
} from '@atlassian/lumina';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { AIAnswerError } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { FollowUpSuggestionFooter } from '../../ai-answer-dialog/ai-answer-footer/followup-suggestion-footer';
import {
	aiAnswerCollapseClicked,
	getSearchAIAnalyticsContextData,
	onAIAnswerResultCompleteShown,
	onAIAnswerResultFirstByteShown,
	onAIAnswerSourceClick,
	SearchAIAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import { SearchAIAnalyticsContext } from '../../ai-answer-dialog/search-ai-analytics-context';
import {
	type commonAttributesType,
	type NLPSearchResultSource,
	type TraceHeaders,
} from '../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import type { QueryIntent } from '../../constants';
import { DynamicLayout } from '../../dynamic-layout/dynamic-layout';
import { DynamicLayoutSourcesDropdown } from '../../dynamic-layout/ui/component/dynamic-layout-sources-dropdown';
import { useBufferedContent } from '../common/hooks/use-buffered-content';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { AnimatedSingleLineShowMore } from '../common/ui/animated-single-line-show-more';
import { OpenChatInputBox } from '../common/ui/open-chat-input-box';
import { SmartAnswerCardBodyGenerating } from '../common/ui/smart-answer-card-body-generating';

import { messages } from './messages';

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	container: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
	interactive: {
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
	expanded: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
	root: {
		minHeight: '198px',
	},
	fullHeight: {
		height: '100%',
	},
	followUpChatInputSectionStyles: {
		marginTop: token('space.400'),
	},
	disclaimerWrapperStyles: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
	},
	headerContainer: {
		minHeight: '40px',
		paddingBottom: token('space.100'),
	},
	loadingSkeletonContainer: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
});

type SmartAnswerCardDynamicLayoutProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	queryIntent?: QueryIntent;
};

function SmartAnswerCardDynamicLayoutComponent({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardDynamicLayoutProps) {
	const [collapsed, setCollapsed] = useState(true);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);
	const [showAnimation, setShowAnimation] = useState(false);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const firstByteEventFiredRef = useRef(false);

	const { commonAttributes, hasError, isAnswered, loading, partialResponse, traceHeaders } =
		useAIAnswerContext();
	const { queryHash, searchSessionId, answerString, sources, errorStateSuggestedQueries } =
		commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	// Refs for content truncation detection
	const isVisibleTextCompleteRef = useRef(false);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached]);

	// Track if we should show animations (if loading or partialResponse are true)
	useEffect(() => {
		if (!showAnimation && loading && partialResponse) {
			setShowAnimation(true);
		}
	}, [loading, partialResponse, showAnimation]); // use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
	);

	// Complete answer analytics
	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	// SAIN usage event
	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	// UFO resize observer for streaming
	const resizeObserverCallback = useCallback(() => {
		if (
			!isVisibleTextCompleteRef.current &&
			renderedContentRef.current &&
			contentContainerRef.current
		) {
			const renderedContentRefHeight = renderedContentRef.current.getBoundingClientRect().height;
			const contentContainerRefHeight = contentContainerRef.current.getBoundingClientRect().height;
			const isVisibleTextComplete = renderedContentRefHeight > contentContainerRefHeight;
			if (isVisibleTextComplete) {
				handleVisibleTextComplete();
				isVisibleTextCompleteRef.current = true;
			}
		}
	}, [handleVisibleTextComplete]);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useDelayedResizeObserver(renderedContentRef, resizeObserverCallback);

	const bufferedContent = useBufferedContent(streamingTextToDisplay, isAnswered && !loading);

	useEffect(() => {
		if (bufferedContent.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [bufferedContent, commonAttributes, fireAnalyticsEvent]);

	// Use answerString when complete, bufferedContent when streaming
	const contentToRender = isAnswered ? answerString : bufferedContent;

	// Experiments
	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const showReadMoreButton = collapsed && isContentTruncated;

	// Single return statement with React component for main content
	const isStreaming = !isAnswered && (loading || loading === undefined);

	// Early return for error state
	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	// Early return for generating state
	if (!isAnswered && (loading || loading === undefined)) {
		if (!partialResponse || bufferedContent.length < 1) {
			return (
				<Box xcss={styles.fullWidth}>
					<SmartAnswerCardBodyGenerating
						heightOverride={SAINFeatureControl.isFullHeightLuminaEnabled() ? 'lumina' : undefined}
					/>
					<AnimatedSingleLineShowMore isStreaming showMoreButtonVisible={false} />
				</Box>
			);
		}
	}

	return (
		<Box xcss={styles.fullWidth}>
			<UFOSegment name="search-ai-dialog-all-text">
				<UFOSegment name="search-ai-dialog-visible-text">
					{isVisibleTextComplete ? null : (
						<UFOLoadHold name="search-ai-dialog-visible-text-loading" />
					)}
					{fg('platform_ufo_custom_cohort_data_migration') ? (
						<>
							<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
							<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
							<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
						</>
					) : (
						<UFOCustomData
							data={{
								queryHash,
								searchSessionId,
								queryIntent,
							}}
						/>
					)}
					{/* Main content */}
					<SmartAnswerCardContent
						isStreaming={isStreaming}
						collapsed={collapsed}
						isContentTruncated={isContentTruncated}
						showReadMoreButton={showReadMoreButton}
						handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
						contentContainerRef={contentContainerRef}
						renderedContentRef={renderedContentRef}
						sources={sources}
						searchSessionId={searchSessionId}
						sourceClickCallback={sourceClickCallback}
						errorStateSuggestedQueries={errorStateSuggestedQueries}
						serveErrorStateSuggestedQueriesExp={serveErrorStateSuggestedQueriesExp}
						commonAttributes={commonAttributes}
						contentToRender={contentToRender}
						showAnimation={showAnimation}
						traceHeaders={traceHeaders}
					/>
				</UFOSegment>
			</UFOSegment>
		</Box>
	);
}

export function SmartAnswerCardContent({
	isStreaming,
	collapsed,
	isContentTruncated,
	showReadMoreButton,
	handleExpandCollapseButtonClick,
	contentContainerRef,
	renderedContentRef,
	errorStateSuggestedQueries,
	serveErrorStateSuggestedQueriesExp,
	commonAttributes,
	contentToRender,
	traceHeaders,
}: {
	isStreaming: boolean;
	collapsed: boolean;
	isContentTruncated: boolean;
	showReadMoreButton: boolean;
	handleExpandCollapseButtonClick: () => void;
	contentContainerRef: React.RefObject<HTMLDivElement>;
	renderedContentRef: React.RefObject<HTMLDivElement>;
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	sourceClickCallback: (index: number, source: NLPSearchResultSource, hostname?: string) => void;
	errorStateSuggestedQueries: string[];
	serveErrorStateSuggestedQueriesExp: boolean;
	commonAttributes: commonAttributesType;
	contentToRender: string;
	showAnimation: boolean;
	traceHeaders?: TraceHeaders;
}): React.JSX.Element {
	const { dynamicLayoutSubAgentMessages, userDetails } = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const handleLuminaEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			if (
				event.payload.action === 'interacted' &&
				event.payload.attributes.componentType === 'smartLink' &&
				event.payload.attributes.interactionType === 'clicked'
			) {
				const attributes = event.payload.attributes as LuminaInteractedAttributesType;
				fireSAINUsageEvent({
					metCriteria: SainAction.SOURCE_CLICKED,
					commonAttributes,
					sourceProduct: attributes.product ?? undefined,
				});
			}
		},
		[fireSAINUsageEvent, commonAttributes],
	);

	return (
		<Box>
			<AICollapse
				isCollapsed={collapsed && (isStreaming || isContentTruncated)}
				onExpandCollapse={handleExpandCollapseButtonClick}
				ref={contentContainerRef}
				showReadMoreButton={showReadMoreButton}
				luminaAnswerType="LUMINA"
			>
				<Box>
					<SearchAIAnalyticsContext
						data={getSearchAIAnalyticsContextData({
							location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
							commonAttributes,
						})}
					>
						<SmartCardProvider>
							<Inline spread="space-between" alignBlock="center" xcss={styles.headerContainer}>
								<Inline alignBlock="center" space="space.050">
									<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
									<Heading as="h1" size="xsmall">
										<FormattedMessage {...messages.smart_answer_card_dynamic_layout_title} />
									</Heading>
								</Inline>
								<DynamicLayoutSourcesDropdown />
							</Inline>
							<Box ref={renderedContentRef} testId="sain-fmp-target">
								{fg('kd_enable_lumina_package') ? (
									<AnalyticsListener channel={LUMINA_ANALYTICS_CHANNEL} onEvent={handleLuminaEvent}>
										<LuminaLayout
											luminaTagResponse={contentToRender}
											luminaSubAgentResponse={
												dynamicLayoutSubAgentMessages as LuminaSubAgentResponseType[]
											}
											isStreaming={isStreaming}
											source="luminaSAIN"
											traceId={traceHeaders?.['X-B3-TraceId']}
											spanId={traceHeaders?.['X-B3-SpanId']}
											cloudId={commonAttributes.cloudId}
											orgId={commonAttributes.orgId}
											requestingUserId={userDetails.id ?? ''}
											sourceProduct={commonAttributes.sourceProduct}
										/>
									</AnalyticsListener>
								) : (
									<DynamicLayout contentToRender={contentToRender} />
								)}
							</Box>
							{isStreaming && (
								<Stack space="space.200" xcss={styles.loadingSkeletonContainer}>
									<Skeleton
										width="100%"
										height="16px"
										color={token('color.skeleton.subtle')}
										borderRadius={'6px'}
									/>
									<Skeleton
										width="100%"
										height="16px"
										color={token('color.skeleton.subtle')}
										borderRadius={'6px'}
									/>
									<Skeleton
										width="75%"
										height="16px"
										color={token('color.skeleton.subtle')}
										borderRadius={'6px'}
									/>
								</Stack>
							)}
						</SmartCardProvider>
					</SearchAIAnalyticsContext>
				</Box>
			</AICollapse>
			{(showReadMoreButton || isStreaming) && SAINFeatureControl.isBorderlessUXEnabled() && (
				<AnimatedSingleLineShowMore
					isStreaming={isStreaming}
					showMoreButtonVisible={showReadMoreButton}
					onClickShowMoreButton={handleExpandCollapseButtonClick}
				/>
			)}

			{/* Footer section - only show when complete */}
			{!isStreaming &&
			(!collapsed ||
				!showReadMoreButton ||
				(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp)) ? (
				<Stack space="space.150" xcss={styles.followUpChatInputSectionStyles}>
					<Inline space="space.100" alignBlock="center">
						<FollowUpSuggestionFooter omitDialogues={true} />
					</Inline>
					<OpenChatInputBox />
					<Box xcss={styles.disclaimerWrapperStyles}>
						<AIDisclaimerAndFeedback
							showFeedbackButtons={!isStreaming}
							showCopyButton={false}
							renderedContentRef={renderedContentRef}
						/>
					</Box>
				</Stack>
			) : null}
		</Box>
	);
}

export function SmartAnswerCardDynamicLayout({
	onFMPReached,
	onAnswerExpandedChange,
	queryIntent,
}: SmartAnswerCardDynamicLayoutProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardDynamicLayoutComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				queryIntent={queryIntent}
			/>
		</UFOSegment>
	);
}
